import React, { useState, useEffect } from 'react';
import { adminApiService } from '../../services/adminApiService';
import { apiService } from '../../services/apiService';
import { FaBell, FaPaperPlane, FaUsers, FaUser, FaEye, FaEyeSlash, FaClock, FaCheckCircle, FaExclamationTriangle, FaTimes, FaEnvelopeOpenText, FaChevronDown, FaChevronRight, FaHistory } from 'react-icons/fa';
import './AdminNotifications.css';

interface NotificationForm {
  message: string;
  recipientUsername?: string;
  recipientId?: number;
  notificationType: string;
  isBroadcast: boolean;
}

interface SentNotification {
  id: number;
  message: string;
  recipientName: string;
  recipientUsername: string;
  createdDate: string;
  messageStatus: string;
  notificationType: string;
  isBroadcast: boolean;
}

interface TradingMessage {
  transactionId: number;
  strategyId: number;
  strategyName: string;
  symbol: string;
  tradeType: string; // LONG / SHORT / CLOSE
  amount: number;
  entryPrice: number | null;
  exitPrice: number | null;
  pnl: number | null;
  transactionStatus: string; // OPEN / CLOSED
  positionId: string | null;
  exitReason: string | null;
  closedAt: string | null;
  paperTrading: boolean;
  tradingType: string; // Real Trading / Paper Trading
  transactionDate: string;
  // Missed close detection fields
  shouldHaveClosed?: boolean;
  missedCloseReason?: string | null;
  currentPrice?: number | null;
  priceVsThreshold?: string | null;
}

interface PositionGroup {
  positionId: string;
  symbol: string;
  strategyName: string;
  tradeType: string;
  transactions: TradingMessage[];
  isExpanded: boolean;
}

const AdminNotifications: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'send' | 'sent' | 'messages' | 'audit-trail'>('send');
  const [messagesView, setMessagesView] = useState<'table' | 'audit'>('table');
  const [formData, setFormData] = useState<NotificationForm>({
    message: '',
    recipientUsername: '',
    recipientId: undefined,
    notificationType: 'INFO',
    isBroadcast: false
  });
  const [sentNotifications, setSentNotifications] = useState<SentNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const notificationsPerPage = 5;
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Trading bot messages (position events)
  const [messages, setMessages] = useState<TradingMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [messagesSortColumn, setMessagesSortColumn] = useState<'symbol' | 'strategyName' | 'side' | 'event' | 'time'>('time');
  const [messagesSortDirection, setMessagesSortDirection] = useState<'asc' | 'desc'>('desc');
  const [messagesPage, setMessagesPage] = useState(1);
  const messagesPageSize = 20;
  const [messagesStrategyFilter, setMessagesStrategyFilter] = useState<string>('all');
  const [positionGroups, setPositionGroups] = useState<Map<string, PositionGroup>>(new Map());
  const [expandedPositions, setExpandedPositions] = useState<Set<string>>(new Set());
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });
  
  // Audit trail pagination and sorting
  const [auditTrailPage, setAuditTrailPage] = useState(1);
  const auditTrailPageSize = 20;
  const [auditTrailSortColumn, setAuditTrailSortColumn] = useState<'positionId' | 'symbol' | 'strategyName' | 'type' | 'status' | 'opened' | 'closed' | 'pnl'>('closed');
  const [auditTrailSortDirection, setAuditTrailSortDirection] = useState<'asc' | 'desc'>('desc');
  const [auditTrailStrategyFilter, setAuditTrailStrategyFilter] = useState<string>('all');

  useEffect(() => {
    if (activeTab === 'sent') {
      loadSentNotifications();
    } else if (activeTab === 'messages' || activeTab === 'audit-trail') {
      loadTradingMessages();
    }
    loadUsers();
  }, [activeTab]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await adminApiService.getRealCustomersWithAccounts(0, 1000); // Get real customers from database
      if (response.success && response.data) {
        // Map the customer data to the format we need for the dropdown
        const customerUsers = response.data.content.map((customer: any) => ({
          id: customer.id,
          username: customer.username,
          fullName: `${customer.firstName} ${customer.lastName}`,
          email: customer.email
        }));
        setUsers(customerUsers);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      setError('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadTradingMessages = async () => {
    try {
      setMessagesLoading(true);
      setMessagesError(null);
      setMessagesPage(1);
      // Reuse existing admin reports endpoint – last 30 days of transactions
      const response = await adminApiService.getTradingStrategyRecentTransactions(30);
      if (response.success && response.data) {
        const rows = response.data as any[];
        const mapped: TradingMessage[] = rows.map((row: any) => ({
          transactionId: row.transaction_id,
          strategyId: row.strategy_id,
          strategyName: row.strategy_name,
          symbol: row.symbol,
          tradeType: row.trade_type,
          amount: Number(row.amount ?? 0),
          entryPrice: row.entry_price !== undefined && row.entry_price !== null ? Number(row.entry_price) : null,
          exitPrice: row.exit_price !== undefined && row.exit_price !== null ? Number(row.exit_price) : null,
          pnl: row.pnl !== undefined && row.pnl !== null ? Number(row.pnl) : null,
          transactionStatus: row.transaction_status,
          positionId: row.position_id || null,
          exitReason: row.exit_reason || null,
          closedAt: row.closed_at || null,
          paperTrading: Boolean(row.paper_trading),
          tradingType: row.trading_type,
          transactionDate: row.transaction_date,
          // Missed close detection fields
          shouldHaveClosed: Boolean(row.should_have_closed ?? false),
          missedCloseReason: row.missed_close_reason || null,
          currentPrice: row.current_price ? Number(row.current_price) : null,
          priceVsThreshold: row.price_vs_threshold || null
        }));

        setMessages(mapped);
        
        // Group by position_id for audit trail
        const groups = new Map<string, PositionGroup>();
        mapped.forEach((msg) => {
          if (msg.positionId) {
            if (!groups.has(msg.positionId)) {
              groups.set(msg.positionId, {
                positionId: msg.positionId,
                symbol: msg.symbol,
                strategyName: msg.strategyName,
                tradeType: msg.tradeType,
                transactions: [],
                isExpanded: false,
              });
            }
            groups.get(msg.positionId)!.transactions.push(msg);
          }
        });
        
        // Sort transactions within each group by date
        groups.forEach((group) => {
          group.transactions.sort((a, b) => 
            new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()
          );
        });
        
        setPositionGroups(groups);
        setAuditTrailPage(1); // Reset to first page when data is reloaded
      } else if (!response.success) {
        setMessagesError(response.error || 'Failed to load trading messages');
      }
    } catch (error) {
      console.error('Failed to load trading messages:', error);
      setMessagesError('Failed to load trading messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  const getMessageEventLabel = (msg: TradingMessage) => {
    // Primary check: Use transactionStatus to determine if position is open or closed
    // For LONG/SHORT records: OPEN status = Position Opened, CLOSED status = Position Closed
    // For CLOSE records: Always show Position Closed
    
    if (msg.tradeType === 'CLOSE') {
      return 'Position Closed';
    }
    
    if (msg.transactionStatus === 'OPEN' && (msg.tradeType === 'LONG' || msg.tradeType === 'SHORT')) {
      return 'Position Opened';
    }
    
    if (msg.transactionStatus === 'CLOSED') {
      return 'Position Closed';
    }
    
    // Fallback: If we have closedAt, it's likely closed
    if (msg.closedAt !== null && msg.closedAt !== undefined) {
      return 'Position Closed';
    }
    
    // Default fallback
    return 'Position Update';
  };

  const getMessageSideLabel = (msg: TradingMessage) => {
    if (!msg.tradeType) return '';
    const upper = msg.tradeType.toUpperCase();
    if (upper === 'CLOSE') return 'CLOSE';
    return upper;
  };

  const handleMessagesSort = (column: 'symbol' | 'strategyName' | 'side' | 'event') => {
    if (messagesSortColumn === column) {
      setMessagesSortDirection(messagesSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setMessagesSortColumn(column);
      setMessagesSortDirection(column === 'symbol' || column === 'strategyName' ? 'asc' : 'desc');
    }
    setMessagesPage(1);
  };

  const handleMessagesStrategyFilterChange = (strategy: string) => {
    setMessagesStrategyFilter(strategy);
    setMessagesPage(1); // Reset to first page when filter changes
  };

  // Get unique strategies from messages
  const getUniqueMessageStrategies = (): string[] => {
    const strategies = new Set<string>();
    messages.forEach((msg) => {
      if (msg.strategyName) {
        strategies.add(msg.strategyName);
      }
    });
    return Array.from(strategies).sort();
  };

  const getSortedMessages = () => {
    // First filter by strategy if needed
    let filtered = [...messages];
    if (messagesStrategyFilter !== 'all') {
      filtered = filtered.filter((msg) => msg.strategyName === messagesStrategyFilter);
    }

    // Then sort
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      let aVal = '';
      let bVal = '';

      switch (messagesSortColumn) {
        case 'symbol':
          aVal = a.symbol || '';
          bVal = b.symbol || '';
          break;
        case 'strategyName':
          aVal = a.strategyName || '';
          bVal = b.strategyName || '';
          break;
        case 'side':
          aVal = getMessageSideLabel(a);
          bVal = getMessageSideLabel(b);
          break;
        case 'event':
          aVal = getMessageEventLabel(a);
          bVal = getMessageEventLabel(b);
          break;
        case 'time':
        default: {
          const da = new Date(a.transactionDate).getTime();
          const db = new Date(b.transactionDate).getTime();
          return messagesSortDirection === 'asc' ? da - db : db - da;
        }
      }

      const cmp = aVal.localeCompare(bVal);
      return messagesSortDirection === 'asc' ? cmp : -cmp;
    });

    return sorted;
  };

  // Derived pagination data for Messages tab
  const sortedMessages = getSortedMessages();
  const totalMessagePages = Math.ceil(sortedMessages.length / messagesPageSize) || 1;
  const safeMessagesPage = Math.min(Math.max(messagesPage, 1), totalMessagePages);
  const messagesStartIndex = (safeMessagesPage - 1) * messagesPageSize;
  const messagesEndIndex = messagesStartIndex + messagesPageSize;
  const paginatedMessages = sortedMessages.slice(messagesStartIndex, messagesEndIndex);

  const loadSentNotifications = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/notifications/admin/sent');
      if (response.success && response.data) {
        setSentNotifications(response.data);
      }
    } catch (error) {
      console.error('Failed to load sent notifications:', error);
      setError('Failed to load sent notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const endpoint = formData.isBroadcast ? '/api/notifications/broadcast' : '/api/notifications/send';
      const payload = {
        message: formData.message,
        notificationType: formData.notificationType,
        isBroadcast: formData.isBroadcast,
        ...(formData.isBroadcast ? {} : {
          recipientUsername: formData.recipientUsername,
          recipientId: formData.recipientId
        })
      };

      const response = await apiService.post(endpoint, payload);
      
      if (response.success) {
        setSuccess('Notification sent successfully!');
        setFormData({
          message: '',
          recipientUsername: '',
          recipientId: undefined,
          notificationType: 'INFO',
          isBroadcast: false
        });
      } else {
        setError(response.error || 'Failed to send notification');
      }
    } catch (error: any) {
      console.error('Failed to send notification:', error);
      setError(error.response?.data?.message || 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'READ':
        return <FaCheckCircle className="status-icon read" title="Read" />;
      case 'DELIVERED':
        return <FaEye className="status-icon delivered" title="Delivered" />;
      case 'UNREAD':
        return <FaEyeSlash className="status-icon unread" title="Unread" />;
      default:
        return <FaClock className="status-icon pending" title="Pending" />;
    }
  };

  const getNotificationTypeIcon = (type: string) => {
    switch (type) {
      case 'SUCCESS':
        return <FaCheckCircle className="type-icon success" />;
      case 'WARNING':
        return <FaExclamationTriangle className="type-icon warning" />;
      case 'ERROR':
        return <FaTimes className="type-icon error" />;
      default:
        return <FaBell className="type-icon info" />;
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(sentNotifications.length / notificationsPerPage);
  const startIndex = (currentPage - 1) * notificationsPerPage;
  const endIndex = startIndex + notificationsPerPage;
  const currentNotifications = sentNotifications.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleRefresh = () => {
    setCurrentPage(1); // Reset to first page when refreshing
    loadSentNotifications();
  };

  const togglePositionExpansion = (positionId: string) => {
    setExpandedPositions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(positionId)) {
        newSet.delete(positionId);
      } else {
        newSet.add(positionId);
      }
      return newSet;
    });
  };

  const getExitReasonLabel = (reason: string | null) => {
    if (!reason) return null;
    const reasonMap: { [key: string]: string } = {
      'TAKE_PROFIT': 'Take Profit',
      'STOP_LOSS': 'Stop Loss',
      'TRAILING_STOP': 'Trailing Stop',
      'BASKET_PROFIT': 'Basket Profit',
      'UNIVERSAL_BASKET_PROFIT': 'Universal Basket Profit',
      'REBALANCE': 'Rebalance',
      'MANUAL': 'Manual',
      'MICRO_TSL': 'Micro TSL',
    };
    return reasonMap[reason] || reason;
  };

  const handleAuditTrailSort = (column: 'positionId' | 'symbol' | 'strategyName' | 'type' | 'status' | 'opened' | 'closed' | 'pnl') => {
    if (auditTrailSortColumn === column) {
      setAuditTrailSortDirection(auditTrailSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setAuditTrailSortColumn(column);
      setAuditTrailSortDirection(column === 'opened' || column === 'closed' || column === 'pnl' ? 'desc' : 'asc');
    }
    setAuditTrailPage(1); // Reset to first page when sorting changes
  };

  const handleStrategyFilterChange = (strategy: string) => {
    setAuditTrailStrategyFilter(strategy);
    setAuditTrailPage(1); // Reset to first page when filter changes
  };

  // Get unique strategies from position groups
  const getUniqueStrategies = (): string[] => {
    const strategies = new Set<string>();
    positionGroups.forEach((group) => {
      strategies.add(group.strategyName);
    });
    return Array.from(strategies).sort();
  };

  // Filter and sort position groups
  const getSortedPositionGroups = (): PositionGroup[] => {
    let filtered = Array.from(positionGroups.values());
    
    // Apply strategy filter
    if (auditTrailStrategyFilter !== 'all') {
      filtered = filtered.filter((group) => group.strategyName === auditTrailStrategyFilter);
    }
    
    // Apply sorting
    return filtered.sort((a, b) => {
      const openTransactionA = a.transactions.find(t => t.transactionStatus === 'OPEN' || t.tradeType !== 'CLOSE');
      const closeTransactionA = a.transactions.find(t => t.transactionStatus === 'CLOSED' || t.tradeType === 'CLOSE');
      const finalPnLA = closeTransactionA?.pnl || openTransactionA?.pnl || null;
      const sideLabelA = getMessageSideLabel(openTransactionA || a.transactions[0]);
      
      const openTransactionB = b.transactions.find(t => t.transactionStatus === 'OPEN' || t.tradeType !== 'CLOSE');
      const closeTransactionB = b.transactions.find(t => t.transactionStatus === 'CLOSED' || t.tradeType === 'CLOSE');
      const finalPnLB = closeTransactionB?.pnl || openTransactionB?.pnl || null;
      const sideLabelB = getMessageSideLabel(openTransactionB || b.transactions[0]);

      let aVal: any = '';
      let bVal: any = '';

      switch (auditTrailSortColumn) {
        case 'positionId':
          aVal = a.positionId || '';
          bVal = b.positionId || '';
          break;
        case 'symbol':
          aVal = a.symbol || '';
          bVal = b.symbol || '';
          break;
        case 'strategyName':
          aVal = a.strategyName || '';
          bVal = b.strategyName || '';
          break;
        case 'type':
          aVal = sideLabelA || '';
          bVal = sideLabelB || '';
          break;
        case 'status':
          aVal = closeTransactionA ? 'CLOSED' : 'OPEN';
          bVal = closeTransactionB ? 'CLOSED' : 'OPEN';
          break;
        case 'opened':
          aVal = openTransactionA ? new Date(openTransactionA.transactionDate).getTime() : 0;
          bVal = openTransactionB ? new Date(openTransactionB.transactionDate).getTime() : 0;
          break;
        case 'closed':
          const closedDateA = closeTransactionA?.closedAt 
            ? new Date(closeTransactionA.closedAt).getTime()
            : closeTransactionA?.transactionDate
            ? new Date(closeTransactionA.transactionDate).getTime()
            : 0;
          const closedDateB = closeTransactionB?.closedAt 
            ? new Date(closeTransactionB.closedAt).getTime()
            : closeTransactionB?.transactionDate
            ? new Date(closeTransactionB.transactionDate).getTime()
            : 0;
          aVal = closedDateA;
          bVal = closedDateB;
          break;
        case 'pnl':
          aVal = finalPnLA !== null ? finalPnLA : 0;
          bVal = finalPnLB !== null ? finalPnLB : 0;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const cmp = aVal.localeCompare(bVal);
        return auditTrailSortDirection === 'asc' ? cmp : -cmp;
      } else {
        return auditTrailSortDirection === 'asc' ? (aVal - bVal) : (bVal - aVal);
      }
    });
  };

  // Pagination for audit trail
  const sortedPositionGroups = getSortedPositionGroups();
  const totalAuditTrailPages = Math.ceil(sortedPositionGroups.length / auditTrailPageSize) || 1;
  const safeAuditTrailPage = Math.min(Math.max(auditTrailPage, 1), totalAuditTrailPages);
  const auditTrailStartIndex = (safeAuditTrailPage - 1) * auditTrailPageSize;
  const auditTrailEndIndex = auditTrailStartIndex + auditTrailPageSize;
  const paginatedPositionGroups = sortedPositionGroups.slice(auditTrailStartIndex, auditTrailEndIndex);

  return (
    <div className="admin-notifications">
      <div className="notifications-header">
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'send' ? 'active' : ''}`}
            onClick={() => setActiveTab('send')}
          >
            <FaPaperPlane />
            Send Notification
          </button>
          <button
            className={`tab-button ${activeTab === 'sent' ? 'active' : ''}`}
            onClick={() => setActiveTab('sent')}
          >
            <FaBell />
            Sent Notifications
          </button>
          <button
            className={`tab-button ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
          >
            <FaEnvelopeOpenText />
            Messages
          </button>
          <button
            className={`tab-button ${activeTab === 'audit-trail' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit-trail')}
          >
            <FaHistory />
            Audit Trail
          </button>
        </div>
      </div>

      {activeTab === 'send' && (
        <div className="send-notification-section">
          <div className="notification-form-container">
            <h3>Send Notification</h3>
            
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            {success && (
              <div className="success-message">
                {success}
              </div>
            )}

            <form onSubmit={handleSendNotification} className="notification-form">
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isBroadcast}
                    onChange={(e) => setFormData({ ...formData, isBroadcast: e.target.checked })}
                  />
                  Send to all users (Broadcast)
                </label>
              </div>

              {!formData.isBroadcast && (
                <div className="form-group">
                  <label>Recipient</label>
                  <select
                    value={formData.recipientUsername || ''}
                    onChange={(e) => {
                      const selectedUser = users.find(u => u.username === e.target.value);
                      setFormData({
                        ...formData,
                        recipientUsername: e.target.value,
                        recipientId: selectedUser?.id
                      });
                    }}
                    required
                    disabled={loadingUsers}
                  >
                    <option value="">Select a user</option>
                    {loadingUsers ? (
                      <option value="">Loading users...</option>
                    ) : users.length === 0 ? (
                      <option value="">No users found</option>
                    ) : (
                      users.map(user => (
                        <option key={user.id} value={user.username}>
                          {user.fullName} ({user.username})
                        </option>
                      ))
                    )}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Notification Type</label>
                <select
                  value={formData.notificationType}
                  onChange={(e) => setFormData({ ...formData, notificationType: e.target.value })}
                >
                  <option value="INFO">Info</option>
                  <option value="SUCCESS">Success</option>
                  <option value="WARNING">Warning</option>
                  <option value="ERROR">Error</option>
                </select>
              </div>

              <div className="form-group">
                <label>Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Enter your notification message..."
                  rows={4}
                  required
                />
              </div>

              <button type="submit" className="send-button" disabled={loading}>
                {loading ? 'Sending...' : 'Send Notification'}
              </button>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'sent' && (
        <div className="sent-notifications-section">
          <div className="sent-notifications-header">
            <h3>Sent Notifications</h3>
            <button onClick={handleRefresh} className="refresh-button">
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading sent notifications...</p>
            </div>
          ) : sentNotifications.length === 0 ? (
            <div className="empty-state">
              <FaBell className="empty-icon" />
              <h4>No sent notifications</h4>
              <p>Notifications you send will appear here.</p>
            </div>
          ) : (
            <div className="sent-notifications-list">
              {currentNotifications.map((notification) => (
                <div key={notification.id} className="sent-notification-item">
                  <div className="notification-header">
                    <div className="notification-type">
                      {getNotificationTypeIcon(notification.notificationType)}
                      <span className="type-label">{notification.notificationType}</span>
                    </div>
                    <div className="notification-status">
                      {getStatusIcon(notification.messageStatus)}
                      <span className="status-label">{notification.messageStatus}</span>
                    </div>
                  </div>
                  
                  <div className="notification-content">
                    <p className="notification-message">{notification.message}</p>
                  </div>
                  
                  <div className="notification-meta">
                    <div className="recipient-info">
                      {notification.isBroadcast ? (
                        <span className="broadcast-indicator">
                          <FaUsers /> Broadcast to all users
                        </span>
                      ) : (
                        <span className="recipient-indicator">
                          <FaUser /> To: {notification.recipientName} ({notification.recipientUsername})
                        </span>
                      )}
                    </div>
                    <div className="notification-date">
                      Sent: {formatDate(notification.createdDate)}
                    </div>
                  </div>
                </div>
              ))}
              {totalPages > 1 && (
                <div className="pagination-controls" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap' }}>
                  <button onClick={handlePreviousPage} disabled={currentPage === 1} className="pagination-arrow">&#60;</button>
                  <span>Page {currentPage} of {totalPages}</span>
                  <button onClick={handleNextPage} disabled={currentPage === totalPages} className="pagination-arrow">&#62;</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'messages' && (
        <div className="sent-notifications-section">
          <div className="sent-notifications-header">
            <h3>Messages (Trading Bot Position Events)</h3>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label htmlFor="messages-strategy-filter" style={{ fontSize: '12px', fontWeight: 600, color: '#4b5563' }}>
                  Filter by Strategy:
                </label>
                <select
                  id="messages-strategy-filter"
                  value={messagesStrategyFilter}
                  onChange={(e) => handleMessagesStrategyFilterChange(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    minWidth: '200px'
                  }}
                >
                  <option value="all">All Strategies</option>
                  {getUniqueMessageStrategies().map((strategy) => (
                    <option key={strategy} value={strategy}>
                      {strategy}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className="refresh-button"
                onClick={loadTradingMessages}
                disabled={messagesLoading}
              >
                {messagesLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>

          {messagesError && (
            <div className="error-message">
              {messagesError}
            </div>
          )}

          {messagesLoading ? (
            <div className="loading-state">
              <p>Loading trading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="no-notifications">
              <h4>No trading messages</h4>
              <p>Position open/close events from your bots will appear here.</p>
            </div>
          ) : (
            <>
              {isMobile ? (
                <div className="messages-cards">
                  {paginatedMessages.map((msg) => {
                    const eventLabel = getMessageEventLabel(msg);
                    const sideLabel = getMessageSideLabel(msg);
                    const pnlFormatted =
                      msg.pnl === null || msg.pnl === undefined
                        ? '—'
                        : `${msg.pnl >= 0 ? '+' : ''}${msg.pnl.toFixed(2)}`;

                    return (
                      <div
                        key={`${msg.transactionId}-${msg.transactionDate}`}
                        className="message-card"
                      >
                        <div className="message-card-header">
                          <div className="message-card-title">
                            <span className="message-card-symbol">{msg.symbol}</span>
                            <span className="message-card-strategy">{msg.strategyName}</span>
                          </div>
                          <div className="message-card-tags">
                            <span
                              className={`side-badge ${sideLabel === 'LONG' ? 'long' : sideLabel === 'SHORT' ? 'short' : 'neutral'}`}
                            >
                              {sideLabel || '—'}
                            </span>
                            <span className="message-card-event">{eventLabel}</span>
                          </div>
                        </div>
                        <div className="message-card-body">
                          <div className="message-card-row">
                            <span className="message-card-label">Time</span>
                            <span className="message-card-value">
                              {formatDate(msg.transactionDate)}
                            </span>
                          </div>
                          <div className="message-card-row">
                            <span className="message-card-label">Position ID</span>
                            <span className="message-card-value">
                              {msg.positionId || '—'}
                            </span>
                          </div>
                          <div className="message-card-row">
                            <span className="message-card-label">Entry</span>
                            <span className="message-card-value">
                              {msg.entryPrice !== null ? msg.entryPrice : '—'}
                            </span>
                          </div>
                          <div className="message-card-row">
                            <span className="message-card-label">Exit</span>
                            <span className="message-card-value">
                              {msg.exitPrice !== null ? msg.exitPrice : '—'}
                            </span>
                          </div>
                          <div className="message-card-row">
                            <span className="message-card-label">Mode</span>
                            <span className="message-card-value">{msg.tradingType}</span>
                          </div>
                          <div className="message-card-row">
                            <span className="message-card-label">PnL</span>
                            <span
                              className={`message-card-pnl ${
                                msg.pnl !== null && msg.pnl !== undefined
                                  ? msg.pnl >= 0
                                    ? 'pnl-positive'
                                    : 'pnl-negative'
                                  : ''
                              }`}
                            >
                              {pnlFormatted}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="sent-notifications-list">
                  <table className="messages-table">
                    <thead>
                      <tr>
                        <th
                          onClick={() => {
                            setMessagesSortColumn('time');
                            setMessagesSortDirection(
                              messagesSortDirection === 'asc' ? 'desc' : 'asc'
                            );
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          Time
                        </th>
                        <th
                          onClick={() => handleMessagesSort('strategyName')}
                          style={{ cursor: 'pointer' }}
                        >
                          Strategy
                        </th>
                        <th
                          onClick={() => handleMessagesSort('symbol')}
                          style={{ cursor: 'pointer' }}
                        >
                          Symbol
                        </th>
                        <th
                          onClick={() => handleMessagesSort('side')}
                          style={{ cursor: 'pointer' }}
                        >
                          Type
                        </th>
                        <th
                          onClick={() => handleMessagesSort('event')}
                          style={{ cursor: 'pointer' }}
                        >
                          Event
                        </th>
                        <th>Position ID</th>
                        <th>Entry</th>
                        <th>Exit</th>
                        <th>PnL</th>
                        <th>Mode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedMessages.map((msg) => {
                        const eventLabel = getMessageEventLabel(msg);
                        const sideLabel = getMessageSideLabel(msg);
                        const pnlFormatted =
                          msg.pnl === null || msg.pnl === undefined
                            ? '—'
                            : `${msg.pnl >= 0 ? '+' : ''}${msg.pnl.toFixed(2)}`;

                        return (
                          <tr key={`${msg.transactionId}-${msg.transactionDate}`}>
                            <td>{formatDate(msg.transactionDate)}</td>
                            <td>{msg.strategyName}</td>
                            <td>{msg.symbol}</td>
                            <td>
                              <span
                                className={`side-badge ${sideLabel === 'LONG' ? 'long' : sideLabel === 'SHORT' ? 'short' : 'neutral'}`}
                              >
                                {sideLabel || '—'}
                              </span>
                            </td>
                            <td>{eventLabel}</td>
                            <td>{msg.positionId || '—'}</td>
                            <td>{msg.entryPrice !== null ? msg.entryPrice : '—'}</td>
                            <td>{msg.exitPrice !== null ? msg.exitPrice : '—'}</td>
                            <td
                              className={
                                msg.pnl !== null && msg.pnl !== undefined
                                  ? msg.pnl >= 0
                                    ? 'pnl-positive'
                                    : 'pnl-negative'
                                  : ''
                              }
                            >
                              {pnlFormatted}
                            </td>
                            <td>{msg.tradingType}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {messages.length > messagesPageSize && (
                <div className="pagination-controls" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap' }}>
                  <button
                    onClick={() => setMessagesPage((prev) => Math.max(prev - 1, 1))}
                    disabled={messagesPage === 1}
                    className="pagination-arrow"
                  >
                    &#60;
                  </button>
                  <span>
                    Page {messagesPage} of {Math.ceil(messages.length / messagesPageSize) || 1}
                  </span>
                  <button
                    onClick={() =>
                      setMessagesPage((prev) =>
                        prev < Math.ceil(messages.length / messagesPageSize) ? prev + 1 : prev
                      )
                    }
                    disabled={messagesPage >= Math.ceil(messages.length / messagesPageSize)}
                    className="pagination-arrow"
                  >
                    &#62;
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'audit-trail' && (
        <div className="sent-notifications-section">
          <div className="sent-notifications-header">
            <h3>Position Audit Trail</h3>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap', position: 'relative' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                <label htmlFor="strategy-filter" style={{ fontSize: '12px', fontWeight: 600, color: '#4b5563' }}>
                  Filter by Strategy:
                </label>
                <select
                  id="strategy-filter"
                  value={auditTrailStrategyFilter}
                  onChange={(e) => handleStrategyFilterChange(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    minWidth: '200px'
                  }}
                >
                  <option value="all">All Strategies</option>
                  {getUniqueStrategies().map((strategy) => (
                    <option key={strategy} value={strategy}>
                      {strategy}
                    </option>
                  ))}
                </select>
              </div>
              {(() => {
                const sortedGroups = getSortedPositionGroups();
                const openCount = sortedGroups.filter(group => {
                  const closeTransaction = group.transactions.find(t => t.transactionStatus === 'CLOSED' || t.tradeType === 'CLOSE');
                  return !closeTransaction;
                }).length;
                const closedCount = sortedGroups.length - openCount;
                
                // Calculate MISSED count (open positions that should have closed)
                const missedCount = sortedGroups.filter(group => {
                  const closeTransaction = group.transactions.find(t => t.transactionStatus === 'CLOSED' || t.tradeType === 'CLOSE');
                  if (closeTransaction) return false; // Already closed, not missed
                  const openTransaction = group.transactions.find(t => t.transactionStatus === 'OPEN' || t.tradeType !== 'CLOSE');
                  return openTransaction?.shouldHaveClosed === true;
                }).length;
                
                // Create a unique key that changes when counts change - this forces React to unmount old element
                const statusKey = `status-container-${openCount}-${closedCount}-${missedCount}-${sortedGroups.length}`;
                
                return (
                  <div 
                    key={statusKey}
                    style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '6px', 
                      alignItems: 'flex-end', 
                      flexShrink: 0, 
                      minHeight: sortedGroups.length === 0 ? '0' : '30px',
                      position: 'relative',
                      width: 'auto',
                      maxWidth: '100%',
                      isolation: 'isolate',
                      clear: 'both',
                      overflow: 'visible'
                    }}
                  >
                    {/* Status Summary - Calculate OPEN vs CLOSED vs MISSED counts */}
                    {sortedGroups.length > 0 && (
                      <div 
                        key={`badges-${openCount}-${closedCount}-${missedCount}`}
                        style={{ 
                          display: 'flex', 
                          gap: '8px', 
                          alignItems: 'center', 
                          flexWrap: 'nowrap', 
                          whiteSpace: 'nowrap',
                          position: 'relative',
                          width: 'auto',
                          maxWidth: '100%',
                          justifyContent: 'flex-end',
                          isolation: 'isolate',
                          minHeight: '24px',
                          margin: 0,
                          padding: 0,
                          overflow: 'visible'
                        }}
                      >
                        {missedCount > 0 && (
                          <span 
                            key={`missed-badge-${missedCount}`}
                            className="status-badge" 
                            style={{ 
                              whiteSpace: 'nowrap', 
                              flexShrink: 0, 
                              display: 'inline-flex',
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                              border: '1px solid #fbbf24',
                              fontSize: '10px',
                              padding: '2px 6px',
                              paddingBottom: '5px',
                              fontWeight: 600,
                              position: 'relative',
                              zIndex: 1,
                              margin: 0
                            }}
                          >
                            ⚠️ {missedCount} MISSED
                          </span>
                        )}
                        {openCount > 0 && (
                          <span 
                            key={`open-badge-${openCount}`}
                            className="status-badge open" 
                            style={{ 
                              whiteSpace: 'nowrap', 
                              flexShrink: 0, 
                              display: 'inline-flex',
                              paddingBottom: '5px',
                              position: 'relative',
                              zIndex: 1,
                              margin: 0
                            }}
                          >
                            {openCount} OPEN
                          </span>
                        )}
                        {closedCount > 0 && (
                          <span 
                            key={`closed-badge-${closedCount}`}
                            className="status-badge closed" 
                            style={{ 
                              whiteSpace: 'nowrap', 
                              flexShrink: 0, 
                              display: 'inline-flex',
                              paddingBottom: '5px',
                              position: 'relative',
                              zIndex: 1,
                              margin: 0
                            }}
                          >
                            {closedCount} CLOSED
                          </span>
                        )}
                      </div>
                    )}
                    <button
                      className="refresh-button"
                      onClick={loadTradingMessages}
                      disabled={messagesLoading}
                      style={{ flexShrink: 0, marginTop: '6px' }}
                    >
                      {messagesLoading ? 'Refreshing...' : 'Refresh'}
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>

          {messagesError && (
            <div className="error-message">
              {messagesError}
            </div>
          )}

          {messagesLoading ? (
            <div className="loading-state">
              <p>Loading position audit trail...</p>
            </div>
          ) : positionGroups.size === 0 ? (
            <div className="no-notifications">
              <h4>No position data</h4>
              <p>Position lifecycle events will appear here.</p>
            </div>
          ) : (
            <>
              {isMobile ? (
                <div className="audit-trail-cards">
                  {paginatedPositionGroups.map((group) => {
                    const isExpanded = expandedPositions.has(group.positionId);
                    const openTransaction = group.transactions.find(
                      (t) => t.transactionStatus === 'OPEN' || t.tradeType !== 'CLOSE'
                    );
                    const closeTransaction = group.transactions.find(
                      (t) => t.transactionStatus === 'CLOSED' || t.tradeType === 'CLOSE'
                    );
                    const finalPnL = closeTransaction?.pnl ?? openTransaction?.pnl ?? null;
                    const sideLabel = getMessageSideLabel(openTransaction || group.transactions[0]);

                    const statusLabel = closeTransaction
                      ? 'CLOSED'
                      : openTransaction?.shouldHaveClosed
                      ? 'MISSED'
                      : 'OPEN';

                    const statusClass =
                      statusLabel === 'CLOSED'
                        ? 'closed'
                        : statusLabel === 'MISSED'
                        ? 'missed'
                        : 'open';

                    return (
                      <div
                        key={group.positionId}
                        className={`audit-card ${isExpanded ? 'expanded' : ''}`}
                      >
                        <button
                          type="button"
                          className="audit-card-header"
                          onClick={() => togglePositionExpansion(group.positionId)}
                        >
                          <div className="audit-card-header-main">
                            <div className="audit-card-title">
                              <span className="audit-card-position-id">
                                #{group.positionId}
                              </span>
                              <span className="audit-card-symbol">{group.symbol}</span>
                            </div>
                            <div className="audit-card-meta">
                              <span className="audit-card-strategy">{group.strategyName}</span>
                              <span
                                className={`side-badge ${sideLabel === 'LONG' ? 'long' : sideLabel === 'SHORT' ? 'short' : 'neutral'}`}
                              >
                                {sideLabel || '—'}
                              </span>
                              <span className={`status-badge ${statusClass}`}>
                                {statusLabel}
                              </span>
                            </div>
                          </div>
                          <div className="audit-card-header-extra">
                            <div className="audit-card-dates">
                              <span className="audit-card-date-label">
                                Opened:{' '}
                                {openTransaction
                                  ? formatDate(openTransaction.transactionDate)
                                  : '—'}
                              </span>
                              <span className="audit-card-date-label">
                                Closed:{' '}
                                {closeTransaction?.closedAt
                                  ? formatDate(closeTransaction.closedAt)
                                  : closeTransaction?.transactionDate
                                  ? formatDate(closeTransaction.transactionDate)
                                  : '—'}
                              </span>
                            </div>
                            <div
                              className={`audit-card-pnl ${
                                finalPnL !== null
                                  ? finalPnL >= 0
                                    ? 'pnl-positive'
                                    : 'pnl-negative'
                                  : ''
                              }`}
                            >
                              {finalPnL !== null
                                ? `${finalPnL >= 0 ? '+' : ''}${finalPnL.toFixed(2)}`
                                : '—'}
                            </div>
                            <div className="audit-card-chevron">
                              {isExpanded ? (
                                <FaChevronDown style={{ fontSize: '12px' }} />
                              ) : (
                                <FaChevronRight style={{ fontSize: '12px' }} />
                              )}
                            </div>
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="audit-card-body">
                            {group.transactions.map((tx) => {
                              const eventLabel = getMessageEventLabel(tx);
                              const pnlFormatted =
                                tx.pnl !== null && tx.pnl !== undefined
                                  ? `${tx.pnl >= 0 ? '+' : ''}${tx.pnl.toFixed(2)}`
                                  : '—';

                              return (
                                <div
                                  key={`${tx.transactionId}-${tx.transactionDate}`}
                                  className="audit-card-event"
                                >
                                  <div className="audit-card-event-main">
                                    <span className="audit-card-event-label">
                                      {eventLabel}
                                    </span>
                                    <span className="audit-card-event-time">
                                      {formatDate(tx.transactionDate)}
                                    </span>
                                  </div>
                                  <div className="audit-card-event-meta">
                                    <span className="audit-card-event-field">
                                      Entry:{' '}
                                      {tx.entryPrice !== null
                                        ? tx.entryPrice.toFixed(8)
                                        : '—'}
                                    </span>
                                    <span className="audit-card-event-field">
                                      Exit:{' '}
                                      {tx.exitPrice !== null
                                        ? tx.exitPrice.toFixed(8)
                                        : '—'}
                                    </span>
                                    <span
                                      className={`audit-card-event-pnl ${
                                        tx.pnl !== null && tx.pnl !== undefined
                                          ? tx.pnl >= 0
                                            ? 'pnl-positive'
                                            : 'pnl-negative'
                                          : ''
                                      }`}
                                    >
                                      PnL: {pnlFormatted}
                                    </span>
                                    {tx.exitReason && (
                                      <span className="exit-reason-badge">
                                        {getExitReasonLabel(tx.exitReason)}
                                      </span>
                                    )}
                                    <span
                                      className={`status-badge ${tx.transactionStatus.toLowerCase()}`}
                                    >
                                      {tx.transactionStatus}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Mobile subtotal for this page */}
                  {(() => {
                    const totalFinalPnL = paginatedPositionGroups.reduce((sum, group) => {
                      const openTransaction = group.transactions.find(
                        (t) => t.transactionStatus === 'OPEN' || t.tradeType !== 'CLOSE'
                      );
                      const closeTransaction = group.transactions.find(
                        (t) => t.transactionStatus === 'CLOSED' || t.tradeType === 'CLOSE'
                      );
                      const finalPnL = closeTransaction?.pnl ?? openTransaction?.pnl ?? null;

                      if (finalPnL === null || finalPnL === undefined || isNaN(finalPnL)) {
                        return sum;
                      }
                      return sum + finalPnL;
                    }, 0);

                    const hasPnL = paginatedPositionGroups.length > 0;
                    const formattedTotal =
                      hasPnL && totalFinalPnL !== 0
                        ? `${totalFinalPnL >= 0 ? '+' : ''}${totalFinalPnL.toFixed(2)}`
                        : hasPnL
                        ? '0.00'
                        : '—';

                    const totalClassName =
                      !hasPnL || totalFinalPnL === 0
                        ? ''
                        : totalFinalPnL > 0
                        ? 'pnl-positive'
                        : 'pnl-negative';

                    return (
                      <div className="audit-card-subtotal">
                        <span>Subtotal Final PnL (this page):</span>
                        <span className={totalClassName}>{formattedTotal}</span>
                      </div>
                    );
                  })()}

                  {sortedPositionGroups.length > auditTrailPageSize && (
                    <div className="pagination-controls audit-pagination-mobile">
                      <button
                        onClick={() =>
                          setAuditTrailPage((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={auditTrailPage === 1}
                        className="pagination-arrow"
                      >
                        &#60;
                      </button>
                      <span>
                        Page {auditTrailPage} of {totalAuditTrailPages}
                      </span>
                      <button
                        onClick={() =>
                          setAuditTrailPage((prev) =>
                            prev < totalAuditTrailPages ? prev + 1 : prev
                          )
                        }
                        disabled={auditTrailPage >= totalAuditTrailPages}
                        className="pagination-arrow"
                      >
                        &#62;
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="audit-trail-container">
                  <table className="messages-table audit-trail-table">
                <thead>
                  <tr>
                    <th style={{ width: '30px' }}></th>
                    <th
                      onClick={() => handleAuditTrailSort('positionId')}
                      style={{ cursor: 'pointer' }}
                      className={auditTrailSortColumn === 'positionId' ? 'sort-active' : ''}
                    >
                      Position ID
                      {auditTrailSortColumn === 'positionId' && (
                        <span style={{ marginLeft: '4px' }}>
                          {auditTrailSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th
                      onClick={() => handleAuditTrailSort('symbol')}
                      style={{ cursor: 'pointer' }}
                      className={auditTrailSortColumn === 'symbol' ? 'sort-active' : ''}
                    >
                      Symbol
                      {auditTrailSortColumn === 'symbol' && (
                        <span style={{ marginLeft: '4px' }}>
                          {auditTrailSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th
                      onClick={() => handleAuditTrailSort('strategyName')}
                      style={{ cursor: 'pointer' }}
                      className={auditTrailSortColumn === 'strategyName' ? 'sort-active' : ''}
                    >
                      Strategy
                      {auditTrailSortColumn === 'strategyName' && (
                        <span style={{ marginLeft: '4px' }}>
                          {auditTrailSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th
                      onClick={() => handleAuditTrailSort('type')}
                      style={{ cursor: 'pointer' }}
                      className={auditTrailSortColumn === 'type' ? 'sort-active' : ''}
                    >
                      Type
                      {auditTrailSortColumn === 'type' && (
                        <span style={{ marginLeft: '4px' }}>
                          {auditTrailSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th
                      onClick={() => handleAuditTrailSort('status')}
                      style={{ cursor: 'pointer' }}
                      className={auditTrailSortColumn === 'status' ? 'sort-active' : ''}
                    >
                      Status
                      {auditTrailSortColumn === 'status' && (
                        <span style={{ marginLeft: '4px' }}>
                          {auditTrailSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th
                      onClick={() => handleAuditTrailSort('opened')}
                      style={{ cursor: 'pointer' }}
                      className={auditTrailSortColumn === 'opened' ? 'sort-active' : ''}
                    >
                      Opened
                      {auditTrailSortColumn === 'opened' && (
                        <span style={{ marginLeft: '4px' }}>
                          {auditTrailSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th
                      onClick={() => handleAuditTrailSort('closed')}
                      style={{ cursor: 'pointer' }}
                      className={auditTrailSortColumn === 'closed' ? 'sort-active' : ''}
                    >
                      Closed
                      {auditTrailSortColumn === 'closed' && (
                        <span style={{ marginLeft: '4px' }}>
                          {auditTrailSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th
                      onClick={() => handleAuditTrailSort('pnl')}
                      style={{ cursor: 'pointer' }}
                      className={auditTrailSortColumn === 'pnl' ? 'sort-active' : ''}
                    >
                      Final PnL
                      {auditTrailSortColumn === 'pnl' && (
                        <span style={{ marginLeft: '4px' }}>
                          {auditTrailSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th>Mode</th>
                  </tr>
                </thead>
                
                <tbody>
                  {paginatedPositionGroups.map((group) => {
                      const isExpanded = expandedPositions.has(group.positionId);
                      const openTransaction = group.transactions.find(t => t.transactionStatus === 'OPEN' || t.tradeType !== 'CLOSE');
                      const closeTransaction = group.transactions.find(t => t.transactionStatus === 'CLOSED' || t.tradeType === 'CLOSE');
                      const finalPnL = closeTransaction?.pnl || openTransaction?.pnl || null;
                      const sideLabel = getMessageSideLabel(openTransaction || group.transactions[0]);
                      const exitReason = closeTransaction?.exitReason;

                      return (
                        <React.Fragment key={group.positionId}>
                          <tr 
                            className="position-group-header"
                            onClick={() => togglePositionExpansion(group.positionId)}
                            style={{ cursor: 'pointer', backgroundColor: isExpanded ? '#f3f4f6' : 'white' }}
                          >
                            <td>
                              {isExpanded ? (
                                <FaChevronDown style={{ fontSize: '12px' }} />
                              ) : (
                                <FaChevronRight style={{ fontSize: '12px' }} />
                              )}
                            </td>
                            <td style={{ fontWeight: 600 }}>{group.positionId}</td>
                            <td>{group.symbol}</td>
                            <td>{group.strategyName}</td>
                            <td>
                              <span
                                className={`side-badge ${sideLabel === 'LONG' ? 'long' : sideLabel === 'SHORT' ? 'short' : 'neutral'}`}
                              >
                                {sideLabel || '—'}
                              </span>
                            </td>
                            {/* Status column for group header rows intentionally left blank.
                                Detailed status and exit reasons are shown in the expanded rows. */}
                            <td></td>
                            <td>
                              {openTransaction ? formatDate(openTransaction.transactionDate) : '—'}
                            </td>
                            <td>
                              {closeTransaction?.closedAt 
                                ? formatDate(closeTransaction.closedAt)
                                : closeTransaction?.transactionDate
                                ? formatDate(closeTransaction.transactionDate)
                                : '—'}
                            </td>
                            <td className={finalPnL !== null ? (finalPnL >= 0 ? 'pnl-positive' : 'pnl-negative') : ''}>
                              {finalPnL !== null ? `${finalPnL >= 0 ? '+' : ''}${finalPnL.toFixed(2)}` : '—'}
                            </td>
                            <td>{openTransaction?.tradingType || group.transactions[0]?.tradingType || '—'}</td>
                          </tr>
                          {isExpanded && (
                            <>
                              {group.transactions.map((tx, idx) => {
                                const eventLabel = getMessageEventLabel(tx);
                                const isLast = idx === group.transactions.length - 1;
                                const pnlFormatted = tx.pnl !== null && tx.pnl !== undefined
                                  ? `${tx.pnl >= 0 ? '+' : ''}${tx.pnl.toFixed(2)}`
                                  : '—';

                                return (
                                  <tr 
                                    key={`${tx.transactionId}-${tx.transactionDate}`}
                                    className="position-transaction-row"
                                    style={{ backgroundColor: '#fafafa' }}
                                  >
                                    <td></td>
                                    <td style={{ paddingLeft: '30px', fontSize: '12px', color: '#6b7280' }}>
                                      {!isLast && '├─ '}
                                      {isLast && '└─ '}
                                      <span style={{ fontWeight: 600 }}>{eventLabel}</span>
                                    </td>
                                    <td style={{ fontSize: '12px' }}>
                                      {formatDate(tx.transactionDate)}
                                    </td>
                                    <td style={{ fontSize: '12px' }}>
                                      {tx.entryPrice !== null ? tx.entryPrice.toFixed(8) : '—'}
                                    </td>
                                    <td style={{ fontSize: '12px' }}>
                                      {tx.exitPrice !== null ? tx.exitPrice.toFixed(8) : '—'}
                                    </td>
                                    <td style={{ fontSize: '12px' }}>
                                      {tx.exitReason && (
                                        <span className="exit-reason-badge">
                                          {getExitReasonLabel(tx.exitReason)}
                                        </span>
                                      )}
                                    </td>
                                    <td style={{ fontSize: '12px' }}>
                                      {tx.closedAt ? formatDate(tx.closedAt) : '—'}
                                    </td>
                                    <td 
                                      className={tx.pnl !== null && tx.pnl !== undefined ? (tx.pnl >= 0 ? 'pnl-positive' : 'pnl-negative') : ''}
                                      style={{ fontSize: '12px' }}
                                    >
                                      {pnlFormatted}
                                    </td>
                                    <td style={{ fontSize: '12px' }}>
                                      <span className={`status-badge ${tx.transactionStatus.toLowerCase()}`}>
                                        {tx.transactionStatus}
                                      </span>
                                    </td>
                                    <td></td>
                                  </tr>
                                );
                              })}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                </tbody>


                <tfoot>
                  {(() => {
                    // Subtotal of Final PnL for the current page (visible rows only)
                    const totalFinalPnL = paginatedPositionGroups.reduce((sum, group) => {
                      const openTransaction = group.transactions.find(
                        (t) => t.transactionStatus === 'OPEN' || t.tradeType !== 'CLOSE'
                      );
                      const closeTransaction = group.transactions.find(
                        (t) => t.transactionStatus === 'CLOSED' || t.tradeType === 'CLOSE'
                      );
                      const finalPnL = closeTransaction?.pnl ?? openTransaction?.pnl ?? null;

                      if (finalPnL === null || finalPnL === undefined || isNaN(finalPnL)) {
                        return sum;
                      }
                      return sum + finalPnL;
                    }, 0);

                    const hasPnL = paginatedPositionGroups.length > 0;
                    const formattedTotal =
                      hasPnL && totalFinalPnL !== 0
                        ? `${totalFinalPnL >= 0 ? '+' : ''}${totalFinalPnL.toFixed(2)}`
                        : hasPnL
                        ? '0.00'
                        : '—';
                    const totalClassName =
                      !hasPnL || totalFinalPnL === 0
                        ? ''
                        : totalFinalPnL > 0
                        ? 'pnl-positive'
                        : 'pnl-negative';

                    return (
                      <tr>
                        <td colSpan={8} style={{ textAlign: 'right', fontWeight: 600 }}>
                          Subtotal Final PnL (this page):
                        </td>
                        <td className={totalClassName} style={{ fontWeight: 600 }}>
                          {formattedTotal}
                        </td>
                        <td></td>
                      </tr>
                    );
                  })()}
                </tfoot>
              </table>
              {sortedPositionGroups.length > auditTrailPageSize && (
                <div
                  className="pagination-controls"
                  style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap' }}
                >
                  <button
                    onClick={() => setAuditTrailPage((prev) => Math.max(prev - 1, 1))}
                    disabled={auditTrailPage === 1}
                    className="pagination-arrow"
                  >
                    &#60;
                  </button>
                  <span>
                    Page {auditTrailPage} of {totalAuditTrailPages}
                  </span>
                  <button
                    onClick={() =>
                      setAuditTrailPage((prev) =>
                        prev < totalAuditTrailPages ? prev + 1 : prev
                      )
                    }
                    disabled={auditTrailPage >= totalAuditTrailPages}
                    className="pagination-arrow"
                  >
                    &#62;
                  </button>
                </div>
              )}
            </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminNotifications; 